import type { CustomerAddress } from '@matix/types';

/**
 * One line of address, the way the delivery picker writes it.
 *
 * The parts are the same and in the same order as `AddressPicker`'s radio
 * rows, so the customer panel and the delivery modal never describe the same
 * address differently. Two field labels change with the LAYOUT rather than the
 * language: an address carrying a governorate came from the Saudi National
 * Address form, where the district is الحي and the house number is رقم المبنى;
 * a legacy Gulf address keeps Area / House.
 *
 * Empty fields drop their label entirely rather than print "—", which is what
 * makes a two-field Kuwaiti address read as "Block 6 · Street 1" instead of a
 * row of dashes.
 */
export function addressSummary(
  a: CustomerAddress,
  t: (b: { en: string; ar: string }) => string,
): string {
  const ksa = Boolean(a.governorate_name);
  const parts: Array<string | null> = [
    a.governorate_name ? `${t({ en: 'City', ar: 'المدينة' })}: ${a.governorate_name.trim()}` : null,
    a.area_name
      ? `${ksa ? t({ en: 'District', ar: 'الحي' }) : t({ en: 'Area', ar: 'المنطقة' })}: ${a.area_name.trim()}`
      : null,
    a.avenue ? `${t({ en: 'Avenue', ar: 'الجادة' })}: ${a.avenue}` : null,
    a.block ? `${t({ en: 'Block', ar: 'القطعة' })}: ${a.block}` : null,
    a.street ? `${t({ en: 'Street', ar: 'الشارع' })}: ${a.street}` : null,
    a.house
      ? `${ksa ? t({ en: 'Building', ar: 'رقم المبنى' }) : t({ en: 'House', ar: 'المنزل' })}: ${a.house}`
      : null,
    a.secondary_number
      ? `${t({ en: 'Additional no.', ar: 'الرقم الفرعي' })}: ${a.secondary_number}`
      : null,
    a.postal_code ? `${t({ en: 'Postal code', ar: 'الرمز البريدي' })}: ${a.postal_code}` : null,
    a.short_address_code
      ? `${t({ en: 'Short address', ar: 'العنوان المختصر' })}: ${a.short_address_code}`
      : null,
    a.floor ? `${t({ en: 'Floor', ar: 'الطابق' })}: ${a.floor}` : null,
    a.flat ? `${t({ en: 'Flat', ar: 'الشقة' })}: ${a.flat}` : null,
    a.address_note ? a.address_note.trim() : null,
  ];
  return parts.filter((p): p is string => Boolean(p)).join(' · ');
}
